import { type CSSProperties, type ReactNode } from 'react';

import { surfaceProps, type WorkspaceAppearance } from '@/entities/appearance';

import styles from './WorkspaceBanner.module.css';

interface Props {
  /** The workspace name (a heading, or an editable title). */
  title: ReactNode;
  /** Small facts about the workspace (type, the viewer's role): a row of chips above the name. */
  chips?: readonly string[];
  /** The main actions (create ...). */
  primary?: ReactNode;
  /** Quiet secondary tools (import, appearance, calendar, trash ...). */
  tools?: ReactNode;
  appearance: WorkspaceAppearance | null | undefined;
  /** The stored header image (or the local preview of a draft one). */
  imageUrl?: string | null;
  /** The older single header tint, used while no header surface is stored. */
  legacyColor?: string | null;
  /** A static copy (the editor's preview): carries no test ids of the live header. */
  preview?: boolean;
  /** The information area under the header (its own surface). */
  children?: ReactNode;
}

/**
 * The workspace header: an identity block (name, facts) and the actions, drawn on ONE surface that
 * fills the whole area (default, solid color, gradient or a photo with a scrim). Under it comes the
 * information area, which has its own surface. Foreground tokens are re-derived per surface
 * (``surfaceProps``) on that element only, so nothing leaks outside the two regions.
 */
export function WorkspaceBanner({
  title,
  chips = [],
  primary,
  tools,
  appearance,
  imageUrl = null,
  legacyColor = null,
  preview = false,
  children,
}: Props) {
  const header = surfaceProps(appearance?.header, imageUrl);
  const tint = !header.custom && legacyColor ? legacyColor : null;
  const kind = header.image
    ? 'image'
    : header.custom
      ? (appearance?.header?.kind ?? 'none')
      : 'none';
  return (
    <div className={styles.banner}>
      <header
        className={`${styles.top} ${tint ? styles.tinted : ''}`}
        data-testid={preview ? undefined : 'workspace-header'}
        data-color={
          tint ??
          (appearance?.header?.kind === 'color' ? appearance.header.color : undefined) ??
          undefined
        }
        data-surface={kind}
        style={
          header.style ?? (tint ? ({ '--workspace-color': tint } as CSSProperties) : undefined)
        }
      >
        {header.image && (
          <>
            <img
              className={styles.photo}
              src={header.image.url}
              alt=""
              aria-hidden
              style={{ objectPosition: `${header.image.x * 100}% ${header.image.y * 100}%` }}
            />
            <span
              className={styles.scrim}
              aria-hidden
              style={{ ['--scrim' as string]: String(header.image.overlay) }}
            />
          </>
        )}
        <div className={styles.identity}>
          {chips.length > 0 && (
            <ul className={styles.chips}>
              {chips.map((chip) => (
                <li key={chip} className={styles.chip}>
                  {chip}
                </li>
              ))}
            </ul>
          )}
          {title}
        </div>
        {(primary || tools) && (
          <div className={styles.actions}>
            {primary && <div className={styles.primary}>{primary}</div>}
            {tools && <div className={styles.tools}>{tools}</div>}
          </div>
        )}
      </header>
      {children}
    </div>
  );
}
